import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { getTenantConfig } from '@/lib/tenant-config'
import {
  Button,
  Input,
  Label,
} from '@lead-magnifier/ui'
import { Mail, Loader2, ArrowRight, Briefcase, CheckCircle2 } from 'lucide-react'

export const Route = createFileRoute('/messetasche/')({
  component: MessetascheLookupPage,
})

function MessetascheLookupPage() {
  const tenant = getTenantConfig()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<Array<{
    token: string
    eventName: string
    eventDate: string
    scanCount: number
  }> | null>(null)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return

    setLoading(true)
    setError('')
    setResults(null)

    try {
      // Alle Visitors mit dieser E-Mail finden
      const { data: visitors, error: visitorError } = await supabase
        .from('visitors')
        .select('id, token, events (name, start_date)')
        .eq('email', email.trim().toLowerCase())
        .order('created_at', { ascending: false })

      if (visitorError) throw visitorError

      if (!visitors || visitors.length === 0) {
        setError('Keine Messetasche für diese E-Mail-Adresse gefunden.')
        return
      }

      // Scan-Counts pro Visitor laden
      const enriched = await Promise.all(
        visitors.map(async (v) => {
          const { count } = await supabase
            .from('visitor_scans')
            .select('id', { count: 'exact', head: true })
            .eq('visitor_id', v.id)

          const event = v.events as unknown as { name: string; start_date: string }

          return {
            token: v.token,
            eventName: event.name,
            eventDate: new Date(event.start_date).toLocaleDateString('de-AT', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }),
            scanCount: count || 0,
          }
        })
      )

      // Wenn nur eine Messetasche → direkt weiterleiten
      if (enriched.length === 1) {
        navigate({ to: `/messetasche/${enriched[0].token}` })
        return
      }

      setResults(enriched)
    } catch {
      setError('Fehler beim Suchen. Bitte versuche es erneut.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md animate-fade-in-up">

        {/* Header */}
        <div className="text-center mb-10">
          <div
            className="h-14 w-14 rounded-lg mx-auto mb-5 flex items-center justify-center"
            style={{ backgroundColor: tenant.branding.primary_color }}
          >
            <Briefcase className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">
            Deine Messetasche
          </h1>
          <p className="text-neutral-500 mt-2 text-base leading-relaxed">
            Gib deine E-Mail-Adresse ein, um deine gesammelten Aussteller und Dokumente abzurufen.
          </p>
        </div>

        {/* Form */}
        {!results && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold">
                E-Mail-Adresse
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@beispiel.at"
                required
                className="h-12 text-base"
              />
            </div>

            {error && (
              <div className="rounded-md bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 text-base font-semibold rounded-md"
              style={{ backgroundColor: tenant.branding.primary_color }}
              disabled={!email.trim() || loading}
            >
              {loading ? (
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              ) : (
                <Mail className="h-5 w-5 mr-2" />
              )}
              Messetasche abrufen
            </Button>
          </form>
        )}

        {/* Results — multiple events */}
        {results && (
          <div className="space-y-3 stagger-children">
            <p className="text-sm text-neutral-500 text-center mb-5">
              {results.length} Messetaschen gefunden. Welche möchtest du öffnen?
            </p>
            {results.map((r) => (
              <a
                key={r.token}
                href={`/messetasche/${r.token}`}
                className="flex items-center gap-4 p-4 rounded-md bg-white border border-neutral-200 hover:border-neutral-300 hover:shadow-sm transition-all"
              >
                <div
                  className="h-12 w-12 rounded-md flex items-center justify-center shrink-0"
                  style={{ backgroundColor: tenant.branding.primary_color + '10' }}
                >
                  <Briefcase className="h-6 w-6" style={{ color: tenant.branding.primary_color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-base truncate">{r.eventName}</p>
                  <p className="text-sm text-neutral-500">
                    {r.eventDate} · {r.scanCount} Aussteller
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-neutral-400 shrink-0" />
              </a>
            ))}

            <button
              onClick={() => { setResults(null); setEmail('') }}
              className="w-full text-center text-sm text-neutral-400 hover:text-neutral-600 pt-4 transition-colors"
            >
              Andere E-Mail eingeben
            </button>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-sm text-neutral-300 mt-12">
          {tenant.branding.name}
        </p>
      </div>
    </div>
  )
}
